import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import './PopupTemplates.scss';
import DataGuardAlertInfoBox from '../DataGuardAlertInfoBox';
import { DataGuardAlertButtons } from '../../DataGuardHelperComponents';
import { useSelector } from 'react-redux';
import { dataguard_icons } from "../../../../components/common/utilities";
import { getTotalHierarcyKey } from '../ActionTypes';

/**
 * Renders side popup data similar to SysGuard implementation
 */
const renderSidePopupData = (data) => {
  if (!data || data.length === 0) return null;

  // Sort by totalCount (descending) and pick top 4
  const sortedData = [...data].sort((a, b) => b.totalCount - a.totalCount).slice(0, 4);

  // Calculate total count
  const totalCount = sortedData.reduce((sum, item) => sum + item.totalCount, 0);

  return (
    <div className="side-popup-container">
      {sortedData.map((item, index) => {
        const percentage = (item.totalCount / totalCount) * 100;
        const title = item.dataSourceName ? item.dataSourceName + ' (' + item.totalCount + ')' : 'Unknown';
        return (
          <div key={index} className="side-popup-item">
            <div className="side-popup-header">
              <img
              className="ds-icon"
              title={item.dataSourceName}
              src={dataguard_icons[item.dataSourceType]}
              alt={item.dataSourceType}
            />
            <span
              className="side-popup-name"
              title={title}
            >
              {title}
            </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const renderSidePopupDataPolicy = (data) => {
  if (!data || data.length === 0) return null;

  // Sort by totalCount (descending) and pick top 4
  const sortedData = [...data].sort((a, b) => b.totalCount - a.totalCount).slice(0, 4);

  // Calculate total count
  const totalCount = sortedData.reduce((sum, item) => sum + item.totalCount, 0);

  return (
    <div className="side-popup-container">
      {sortedData.map((item, index) => {
        const percentage = (item.totalCount / totalCount) * 100;
        const title = item.policyName ? item.policyName + ' (' + item.totalCount + ')' : 'Unknown';
        return (
          <div key={index} className="side-popup-item">
            <span
              className="side-popup-name"
              title={title}
            >
              {title}
            </span>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Table with Top Chart Template - Header at top, table below with small chart on top right
 * Used for popups that need a table-focused view with supplementary chart
 */
const TableTopChartTemplate = ({
  header,
  tableConfig,
  topChart,
  pagination,
  onPaginationChange,
  configuration,
}) => {
  const {
    rows = [],
    columns = [],
    totalElements = 0,
    gridProps = {}
  } = tableConfig || {};

  const gridHeight = useMemo(() => {
    const ROW_HEIGHT = 52;
    const HEADER_HEIGHT = 56;
    const FOOTER_HEIGHT = 56;
    const MAX_ROWS = 10;
    const rowCount = rows.length;
    const visibleRows = Math.min(rowCount, MAX_ROWS);
    return HEADER_HEIGHT + (visibleRows * ROW_HEIGHT) + FOOTER_HEIGHT;
  }, [rows.length]);

  const containerStyle = useMemo(() => ({
    height: gridHeight,
    width: '100%'
  }), [gridHeight]);

  const [selectedRow, setSelectedRow] = useState(null);
  const popupDetails = useSelector(state => state.DataguardReducer.popupDetails);
  const popupAggregationData = useSelector(state => state.DataguardReducer.popupAggregationData);
  const loc = getTotalHierarcyKey(popupDetails?.hierarchy);
  
  // Update selectedRow when table data changes to keep alert status in sync
  useEffect(() => {
    if (selectedRow && rows?.length > 0) {
      // Find the updated row data by matching the alertId
      const updatedRow = rows.find(row => row.alertId === selectedRow.alertId);
      if (updatedRow && updatedRow.alertStatus !== selectedRow.alertStatus) {
        // Update selectedRow with the fresh data including updated alertStatus
        setSelectedRow(updatedRow);
      }
    }
  }, [rows, selectedRow]);
  
  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const defaultGridProps = useMemo(() => ({
    pagination: true,
    paginationMode: "server",
    rowsPerPageOptions: [5, 10, 20, 50, 100],
    disableColumnFilter: true,
    disableColumnSelector: true,
    disableColumnMenu: true,
    localeText: { noRowsLabel: "No Data Available" },
    sx: {
      minWidth: 800,
      overflowY: "auto",
      "& .MuiDataGrid-virtualScroller": {
        overflowY: "auto",
      },
    },
    ...gridProps
  }), [gridProps]);

  return (
    <div className="popup-content split-layout">
      {header}
      <div className="second-container">
        <div className="left-side-panel">
          <div style={containerStyle}>
            <DataGrid
              rows={rows}
              columns={columns}
              rowCount={totalElements}
              page={pagination.page}
              pageSize={pagination.pageSize}
              onRowClick={handleRowClick}
              onPageChange={(newPage) => onPaginationChange({ ...pagination, page: newPage })}
              onPageSizeChange={(newPageSize) => onPaginationChange({ ...pagination, pageSize: newPageSize })}
              {...defaultGridProps}
            />
          </div>
        </div>
        {configuration.sideChart && <div className="right-side-panel alert-info-box">
          <div className='dataguard-location-side-chart'>
            {popupAggregationData && (
              <div className="side-popup-data-section">
                {popupAggregationData[0]?.policyName ? renderSidePopupDataPolicy(popupAggregationData): renderSidePopupData(popupAggregationData)}
              </div>
            )}
            <div className="dataguard-info-box-container">
              <DataGuardAlertButtons alertData={selectedRow} dataSourceId={popupDetails.dataSource_DATA} />
              <DataGuardAlertInfoBox
                data={
                  {
                    alertInfo:selectedRow?.alertViolation,
                    alertId: selectedRow?.alertId,
                    ipInfo: selectedRow?.clientIp,
                    userName: selectedRow?.username,

                  }
                }
                loc={loc}
                parentData={
                  {
                    status: selectedRow?.alertStatus,
                    ids: [selectedRow?.alertId],
                  }
                }
              />
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default TableTopChartTemplate;